import type { BilletterieOrder } from '@cpce/shared';
import Decimal from 'decimal.js';
import { Link2 } from 'lucide-react';
import { useAttachBilletterieOrder } from '@/api/billetterie-orders/billetterie-orders.mutations';
import { Badge, BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmButton } from '@/components/ConfirmButton';
import { StyledLink } from '@/components/StyledLink';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { formatDecimalAsPrice } from '@/utils/string.utils';
import { CUSTOMER_PAYMENT_MODES, isAttachable, reglementLabel } from '../billetterie-orders.utils';

interface AttachOrderCellProps {
  order: BilletterieOrder;
  /** The salarié it is booked on; null while none is picked for an order a CSE agent placed. */
  cardId: number | null;
}

/** Books a deferred order in CSE accounting, then opens its règlement. */
export function AttachOrderCell(props: AttachOrderCellProps) {
  const { order, cardId } = props;
  const navigate = useCommiteeSlugNavigate();
  const { mutateAsync, isPending } = useAttachBilletterieOrder();

  if (order.attachedParticipationId !== null) {
    return (
      <div className="flex flex-col items-start gap-1">
        <StyledLink
          text={`Dossier n° ${order.attachedParticipationId}`}
          navigateTo={{
            to: '/$commiteeSlug/prestation-participations/{-$id}',
            params: { id: order.attachedParticipationId.toString() },
          }}
        />
        {!isAttachable(order) && (
          <Badge color={BadgeColor.RED}>Annulée dans la billetterie : annulez le dossier</Badge>
        )}
      </div>
    );
  }
  if (!isAttachable(order) || !order.checkoutCompletedAt) return <span>-</span>;

  const checkoutCompletedAt = order.checkoutCompletedAt;
  const amount = formatDecimalAsPrice(new Decimal(order.total).div(100));
  const attach = async () => {
    if (cardId === null) return;
    const { participationId } = await mutateAsync({
      orderId: order.id,
      checkoutCompletedAt,
      cardId,
    });
    navigate({
      to: '/$commiteeSlug/beneficiaries/transactions/create',
      search: {
        employeeId: cardId,
        prestationParticipationIds: [participationId],
        label: reglementLabel(order),
        paymentPreset: order.customerPaymentMode
          ? CUSTOMER_PAYMENT_MODES[order.customerPaymentMode].preset
          : undefined,
      },
    });
  };

  return (
    <ConfirmButton
      confirmationDescription={`La commande ${order.number ?? order.id} (${amount}) sera enregistrée comme une vente au salarié sur la prestation « Billetterie Pass CE », puis vous pourrez saisir son règlement.`}
      confirmationButtonLabel="Rattacher"
      onConfirm={attach}
      button={(openModal) => (
        <Button
          type="button"
          variant="outline"
          icon={Link2}
          isPending={isPending}
          disabled={cardId === null}
          disabledReason={cardId === null ? 'Choisissez d’abord le salarié' : undefined}
          onClick={(event) => {
            event.stopPropagation();
            openModal();
          }}
        >
          Rattacher
        </Button>
      )}
    />
  );
}
